import React, { useContext } from 'react';
import { Rings } from 'react-loader-spinner';
import { STRINGS } from '../constant';
import { ThemeContext } from '../context/ThemeProvider';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faRocket, faCheckCircle, faLightbulb } from '@fortawesome/free-solid-svg-icons';

function Loader(props){
    const { theme } = useContext(ThemeContext);
    
    return (
        <>
        <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ background: theme?.background?.main }}>
          {/* Subtle Animation Elements */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-8 h-8 rounded-full opacity-20"
            style={{ background: theme?.primary?.orange?.[400] || '#f5a17f' }}
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-6 h-6 rounded-full opacity-20"
            style={{ background: theme?.primary?.green?.[400] || '#4ade80' }}
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          />
          
          <motion.div 
            className="w-full max-w-md relative z-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Main Card */}
            <motion.div 
              className="relative overflow-hidden rounded-2xl shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500] || '#0973ba'} 0%, ${theme?.primary?.blue?.[600] || '#0861a0'} 50%, ${theme?.primary?.blue?.[700] || '#064f86'} 100%)`,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              {/* Animated Background Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              
              {/* Content */}
              <div className="relative z-10 p-8 text-center">
                {/* Animated Icon */}
                <motion.div
                  className="mb-6"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full backdrop-blur-sm border border-white/30">
                    <FontAwesomeIcon 
                      icon={faCog} 
                      className="text-white text-2xl"
                    />
                  </div>
                </motion.div>

                {/* Loading Spinner */}
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Rings
                    height="60"
                    width="60"
                    color="white"
                    radius="6"
                    wrapperStyle={{}}
                    wrapperClass=""
                    visible={true}
                    ariaLabel="rings-loading"
                  />
                </motion.div>

                {/* Main Message */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h2 className="text-2xl font-bold text-white mb-3">
                    Loading...
                  </h2>
                  <p className="text-white/90 text-base leading-relaxed mb-6">
                    Please wait while we prepare your content
                  </p>
                </motion.div>

                {/* Progress Indicators */}
                <motion.div
                  className="space-y-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="flex items-center justify-center space-x-3 text-white/80 text-sm">
                    <FontAwesomeIcon icon={faRocket} className="text-white/60" />
                    <span>Loading your data</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3 text-white/80 text-sm">
                    <FontAwesomeIcon icon={faLightbulb} className="text-white/60" />
                    <span>Preparing your experience</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3 text-white/80 text-sm">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-white/60" />
                    <span>Almost ready</span>
                  </div>
                </motion.div>
              </div>

              {/* Bottom Gradient */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
            </motion.div>
          </motion.div>
        </div>
        </>
    );
}

export default Loader;